import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  BookOpen, Clock, MapPin, User, Search, Filter, GraduationCap, Award,
  ChevronDown, Grid, List, Star, Users, Calendar, CheckCircle, X, Loader2, UserCircle
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useAuth } from "../contexts";
import { apiCall } from "../lib/api";
import { toast } from "sonner";

// Comprehensive Ghanaian Curriculum Course Data
const coursesData = [
  // PRESCHOOL / KG COURSES
  {
    id: "pre-1",
    name: "Literacy",
    category: "Core Subjects",
    isElective: false,
    room: "KG Block A",
    schedule: "Mon-Fri 8:30 AM",
    teacher: "[Teacher Name]",
    teacherImage: "",
    description: "Foundation in letters, phonics, and reading through play-based learning and interactive activities.",
    objectives: ["Learn alphabet recognition", "Develop phonics skills", "Begin reading simple words", "Practice writing letters"],
    credits: 3,
    level: "Preschool/KG",
    prerequisites: "None",
    rating: 4.9,
    enrolled: 45
  },
  {
    id: "pre-2",
    name: "Numeracy",
    category: "Core Subjects",
    isElective: false,
    room: "KG Block A",
    schedule: "Mon-Fri 9:30 AM",
    teacher: "[Teacher Name]",
    teacherImage: "",
    description: "Introduction to counting, numbers, and basic math concepts through fun activities.",
    objectives: ["Learn to count", "Recognize numbers", "Understand basic math concepts", "Develop early math skills"],
    credits: 3,
    level: "Preschool/KG",
    prerequisites: "None",
    rating: 4.9,
    enrolled: 45
  },
  {
    id: "pre-3",
    name: "Social Studies",
    category: "Core Subjects",
    isElective: false,
    room: "KG Block B",
    schedule: "Mon/Wed/Fri 10:30 AM",
    teacher: "[Teacher Name]",
    teacherImage: "",
    description: "Explore nature, animals, plants, and Our World Our People (OWOP) through hands-on discovery.",
    objectives: ["Learn about animals and plants", "Discover the natural world", "Understand community", "Appreciate Ghanaian culture"],
    credits: 2,
    level: "Preschool/KG",
    prerequisites: "None",
    rating: 4.8,
    enrolled: 45
  },
  {
    id: "pre-4",
    name: "Computer Science and Engineering",
    category: "STEM",
    isElective: false,
    room: "STEM Room",
    schedule: "Tue/Thu 10:30 AM",
    teacher: "[Teacher Name]",
    teacherImage: "",
    description: "Interactive games, building, STEM activities, and problem solving. Fun learning activities designed to build young minds.",
    objectives: ["Build with blocks and materials", "Develop problem-solving skills", "Learn through interactive games", "Foster creativity and innovation"],
    credits: 2,
    level: "Preschool/KG",
    prerequisites: "None",
    rating: 4.8,
    enrolled: 45
  },
  {
    id: "pre-5",
    name: "Creative Arts",
    category: "Arts",
    isElective: false,
    room: "Art Room",
    schedule: "Fri 11:00 AM",
    teacher: "[Teacher Name]",
    teacherImage: "",
    description: "Express creativity through drawing, painting, music, movement, and drama.",
    objectives: ["Explore art materials", "Learn songs and rhymes", "Develop fine motor skills", "Express creativity freely"],
    credits: 2,
    level: "Preschool/KG",
    prerequisites: "None",
    rating: 4.9,
    enrolled: 45
  },
  {
    id: "pre-6",
    name: "Religious and Moral Education (RME)",
    category: "Core Subjects",
    isElective: false,
    room: "KG Block A",
    schedule: "Wed 11:00 AM",
    teacher: "[Teacher Name]",
    teacherImage: "",
    description: "Building moral character through religious teachings, ethical values, and responsible citizenship for young learners.",
    objectives: ["Learn religious values", "Develop moral character", "Understand right from wrong", "Practice kindness"],
    credits: 2,
    level: "Preschool/KG",
    prerequisites: "None",
    rating: 4.8,
    enrolled: 45
  },

  // BASIC SCHOOL COURSES (Primary 1-6)
  {
    id: "basic-1",
    name: "Mathematics",
    category: "Core Subjects",
    isElective: false,
    room: "Room 101",
    schedule: "Mon-Fri 8:00 AM",
    teacher: "[Teacher Name]",
    teacherImage: "",
    description: "Building strong mathematical foundations through numbers, operations, measurement, and problem-solving activities.",
    objectives: ["Master basic operations", "Understand fractions and decimals", "Learn measurement concepts", "Develop problem-solving skills"],
    credits: 4,
    level: "Basic School (Primary 1-6)",
    prerequisites: "Numeracy completion",
    rating: 4.8,
    enrolled: 85
  },
  {
    id: "basic-2",
    name: "English Language",
    category: "Core Subjects",
    isElective: false,
    room: "Room 102",
    schedule: "Mon-Fri 9:00 AM",
    teacher: "[Teacher Name]",
    teacherImage: "",
    description: "Comprehensive English program covering reading, writing, grammar, and oral communication skills.",
    objectives: ["Develop reading fluency", "Master grammar rules", "Improve writing skills", "Build vocabulary"],
    credits: 4,
    level: "Basic School (Primary 1-6)",
    prerequisites: "Literacy completion",
    rating: 4.9,
    enrolled: 85
  },
  {
    id: "basic-3",
    name: "Natural Science",
    category: "Core Subjects",
    isElective: false,
    room: "Science Room",
    schedule: "Mon/Wed/Fri 10:00 AM",
    teacher: "[Teacher Name]",
    teacherImage: "",
    description: "Explore the natural world through experiments, observations, and scientific inquiry. Introduction to living things, matter, and energy.",
    objectives: ["Understand living things", "Learn about matter and energy", "Conduct simple experiments", "Develop scientific thinking"],
    credits: 3,
    level: "Basic School (Primary 1-6)",
    prerequisites: "None",
    rating: 4.7,
    enrolled: 85
  },
  {
    id: "basic-4",
    name: "Religious and Moral Education (RME)",
    category: "Core Subjects",
    isElective: false,
    room: "Room 104",
    schedule: "Tue/Thu 10:00 AM",
    teacher: "[Teacher Name]",
    teacherImage: "",
    description: "Building moral character through religious teachings, ethical values, and responsible citizenship.",
    objectives: ["Learn religious values", "Develop moral character", "Understand ethical principles", "Practice good citizenship"],
    credits: 2,
    level: "Basic School (Primary 1-6)",
    prerequisites: "None",
    rating: 4.8,
    enrolled: 85
  },
  {
    id: "basic-5",
    name: "Creative Arts",
    category: "Arts",
    isElective: false,
    room: "Art Studio",
    schedule: "Wed 2:00 PM",
    teacher: "[Teacher Name]",
    teacherImage: "",
    description: "Express creativity through visual arts, music, dance, and drama. Traditional Ghanaian arts and contemporary techniques.",
    objectives: ["Explore various art mediums", "Learn traditional Ghanaian arts", "Develop musical abilities", "Build confidence through performance"],
    credits: 2,
    level: "Basic School (Primary 1-6)",
    prerequisites: "None",
    rating: 4.7,
    enrolled: 85
  },
  {
    id: "basic-6",
    name: "History",
    category: "Core Subjects",
    isElective: false,
    room: "Room 105",
    schedule: "Tue/Thu 11:00 AM",
    teacher: "[Teacher Name]",
    teacherImage: "",
    description: "Discover Ghana's rich history, from ancient kingdoms to modern independence. Learn about important events and figures.",
    objectives: ["Learn Ghanaian history", "Understand cultural heritage", "Study important historical figures", "Appreciate national identity"],
    credits: 2,
    level: "Basic School (Primary 1-6)",
    prerequisites: "None",
    rating: 4.6,
    enrolled: 85
  },
  {
    id: "basic-7",
    name: "French",
    category: "Languages",
    isElective: false,
    room: "Room 106",
    schedule: "Mon/Wed 2:00 PM",
    teacher: "[Teacher Name]",
    teacherImage: "",
    description: "Introduction to French language with focus on vocabulary, basic grammar, and simple conversations.",
    objectives: ["Build French vocabulary", "Learn basic greetings", "Understand simple sentences", "Develop listening skills"],
    credits: 2,
    level: "Basic School (Primary 1-6)",
    prerequisites: "None",
    rating: 4.5,
    enrolled: 85
  },
  {
    id: "basic-8",
    name: "Fante",
    category: "Languages",
    isElective: false,
    room: "Room 107",
    schedule: "Tue/Thu 2:00 PM",
    teacher: "[Teacher Name]",
    teacherImage: "",
    description: "Learn to read, write, and speak Fante. Preserve cultural heritage while developing communication skills.",
    objectives: ["Read and write in Fante", "Understand proverbs and idioms", "Communicate effectively", "Appreciate cultural heritage"],
    credits: 2,
    level: "Basic School (Primary 1-6)",
    prerequisites: "None",
    rating: 4.6,
    enrolled: 85
  },
  {
    id: "basic-9",
    name: "Computer Science, Coding and Engineering",
    category: "Technology",
    isElective: false,
    room: "ICT Lab",
    schedule: "Fri 10:00 AM",
    teacher: "[Teacher Name]",
    teacherImage: "",
    description: "Introduction to computers, coding fundamentals, engineering concepts, and digital literacy for the modern world.",
    objectives: ["Learn computer basics", "Develop coding skills", "Introduction to engineering", "Use educational software"],
    credits: 2,
    level: "Basic School (Primary 1-6)",
    prerequisites: "None",
    rating: 4.8,
    enrolled: 85
  },

  // JHS COURSES
  {
    id: "jhs-1",
    name: "Mathematics",
    category: "Core Subjects",
    isElective: false,
    room: "Room 201",
    schedule: "Mon/Wed/Fri 8:00 AM",
    teacher: "[Teacher Name]",
    teacherImage: "",
    description: "Advanced mathematics covering algebra, geometry, trigonometry, and statistics. Preparation for BECE examinations.",
    objectives: ["Master algebraic expressions", "Understand geometric principles", "Apply statistics", "Prepare for BECE"],
    credits: 4,
    level: "JHS 1-3",
    prerequisites: "Basic School Mathematics",
    rating: 4.8,
    enrolled: 120
  },
  {
    id: "jhs-2",
    name: "English Language",
    category: "Core Subjects",
    isElective: false,
    room: "Room 202",
    schedule: "Mon/Wed/Fri 9:00 AM",
    teacher: "[Teacher Name]",
    teacherImage: "",
    description: "Advanced English focusing on comprehension, essay writing, grammar, and oral communication for BECE success.",
    objectives: ["Improve comprehension", "Master grammar", "Develop essay skills", "Enhance oral abilities"],
    credits: 4,
    level: "JHS 1-3",
    prerequisites: "Basic School English",
    rating: 4.9,
    enrolled: 125
  },
  {
    id: "jhs-3",
    name: "Integrated Science",
    category: "Core Subjects",
    isElective: false,
    room: "Lab 201",
    schedule: "Tue/Thu 8:00 AM",
    teacher: "[Teacher Name]",
    teacherImage: "",
    description: "Comprehensive science covering biology, chemistry, and physics. Hands-on experiments and theoretical learning.",
    objectives: ["Understand biological concepts", "Learn chemical reactions", "Explore physics principles", "Conduct experiments"],
    credits: 4,
    level: "JHS 1-3",
    prerequisites: "Natural Science",
    rating: 4.7,
    enrolled: 118
  },
  {
    id: "jhs-4",
    name: "Social Studies",
    category: "Core Subjects",
    isElective: false,
    room: "Room 203",
    schedule: "Tue/Thu 10:00 AM",
    teacher: "[Teacher Name]",
    teacherImage: "",
    description: "Study Ghana's history, geography, government, and civic responsibilities. Understanding social issues and global awareness.",
    objectives: ["Learn Ghanaian history", "Understand government", "Explore environmental issues", "Develop civic responsibility"],
    credits: 3,
    level: "JHS 1-3",
    prerequisites: "History",
    rating: 4.6,
    enrolled: 115
  },
  {
    id: "jhs-5",
    name: "Religious and Moral Education (RME)",
    category: "Core Subjects",
    isElective: false,
    room: "Room 204",
    schedule: "Mon/Wed 11:00 AM",
    teacher: "[Teacher Name]",
    teacherImage: "",
    description: "Advanced religious and moral education exploring world religions, ethics, and responsible citizenship.",
    objectives: ["Understand world religions", "Develop moral values", "Apply ethical principles", "Promote tolerance"],
    credits: 2,
    level: "JHS 1-3",
    prerequisites: "Basic RME",
    rating: 4.8,
    enrolled: 122
  },
  {
    id: "jhs-6",
    name: "Creative Arts",
    category: "Arts",
    isElective: false,
    room: "Art Studio",
    schedule: "Wed 2:00 PM",
    teacher: "[Teacher Name]",
    teacherImage: "",
    description: "Advanced creative arts including visual arts, music, dance, and drama. Traditional and contemporary artistic expression.",
    objectives: ["Master art techniques", "Learn traditional arts", "Develop performance skills", "Express creativity"],
    credits: 2,
    level: "JHS 1-3",
    prerequisites: "Basic Creative Arts",
    rating: 4.7,
    enrolled: 95
  },
  {
    id: "jhs-7",
    name: "Career Technology",
    category: "Vocational",
    isElective: false,
    room: "Tech Workshop",
    schedule: "Tue/Fri 1:00 PM",
    teacher: "[Teacher Name]",
    teacherImage: "",
    description: "Practical skills in technical drawing, woodwork, and metalwork. Foundation for technical career paths.",
    objectives: ["Learn technical drawing", "Practice woodworking", "Understand metalwork", "Develop design skills"],
    credits: 3,
    level: "JHS 1-3",
    prerequisites: "None",
    rating: 4.6,
    enrolled: 78
  },
  {
    id: "jhs-8",
    name: "French",
    category: "Languages",
    isElective: false,
    room: "Room 206",
    schedule: "Mon/Thu 2:00 PM",
    teacher: "[Teacher Name]",
    teacherImage: "",
    description: "Advanced French covering grammar, reading, writing, and conversation. Preparation for further language studies.",
    objectives: ["Build advanced vocabulary", "Master grammar structures", "Develop conversation skills", "Understand francophone culture"],
    credits: 2,
    level: "JHS 1-3",
    prerequisites: "Basic French",
    rating: 4.4,
    enrolled: 85
  },
  {
    id: "jhs-9",
    name: "Fante",
    category: "Languages",
    isElective: false,
    room: "Room 207",
    schedule: "Tue/Thu 2:00 PM",
    teacher: "[Teacher Name]",
    teacherImage: "",
    description: "Advanced Fante language studies including literature, proverbs, and cultural expressions.",
    objectives: ["Master reading and writing", "Understand literature", "Learn cultural expressions", "Communicate fluently"],
    credits: 2,
    level: "JHS 1-3",
    prerequisites: "Basic Fante",
    rating: 4.5,
    enrolled: 100
  },
  {
    id: "jhs-10",
    name: "Computer Science, Coding and Engineering",
    category: "Technology",
    isElective: false,
    room: "ICT Lab",
    schedule: "Wed/Fri 10:00 AM",
    teacher: "[Teacher Name]",
    teacherImage: "",
    description: "Advanced computing including programming, software development, engineering concepts, and digital project building.",
    objectives: ["Master programming basics", "Learn engineering principles", "Develop coding skills", "Build digital projects"],
    credits: 3,
    level: "JHS 1-3",
    prerequisites: "Basic Computing",
    rating: 4.9,
    enrolled: 130
  },
  {
    id: "course-11",
    name: "Physical Education",
    category: "Health & Fitness",
    isElective: false,
    room: "Sports Field",
    schedule: "Mon/Fri 3:00 PM",
    teacher: "[Teacher Name]",
    teacherImage: "",
    description: "Physical fitness, sports skills, and healthy lifestyle habits. Athletics, football, volleyball, and team sports.",
    objectives: ["Improve physical fitness", "Learn sports skills", "Understand nutrition", "Develop teamwork"],
    credits: 2,
    level: "All Levels",
    prerequisites: "None",
    rating: 4.8,
    enrolled: 135
  },
  {
    id: "course-13",
    name: "Home Economics",
    category: "Vocational",
    isElective: true,
    room: "Home Ec Lab",
    schedule: "Wed/Fri 11:00 AM",
    teacher: "[Teacher Name]",
    teacherImage: "",
    description: "Learn essential life skills including cooking, nutrition, sewing, and home management. Practical skills for healthy living and household management.",
    objectives: ["Master cooking techniques", "Understand nutrition principles", "Learn basic sewing skills", "Develop home management abilities"],
    credits: 2,
    level: "JHS 1-3",
    prerequisites: "None",
    rating: 4.6,
    enrolled: 88
  },
  {
    id: "course-14",
    name: "Agricultural Science",
    category: "Vocational",
    isElective: true,
    room: "School Farm",
    schedule: "Tue 2:00 PM",
    teacher: "[Teacher Name]",
    teacherImage: "",
    description: "Hands-on experience in crop cultivation, animal husbandry, and sustainable farming practices. Learn about Ghana's agricultural sector and food security.",
    objectives: ["Learn crop cultivation techniques", "Understand animal care basics", "Practice sustainable farming", "Explore agricultural careers"],
    credits: 2,
    level: "JHS 1-3",
    prerequisites: "None",
    rating: 4.4,
    enrolled: 65
  }
];

const categories = ["All", "Core Subjects", "Languages", "Technology", "Vocational", "Arts", "Health & Fitness"];

export const CourseCatalogPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [viewMode, setViewMode] = useState("grid");
  const [sortBy, setSortBy] = useState("name");
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [enrollingCourse, setEnrollingCourse] = useState(null);
  const [loadingEnrollments, setLoadingEnrollments] = useState(false);

  // Fetch user enrollments
  useEffect(() => {
    if (user && token && user.user_type === "student") {
      fetchEnrollments();
    }
  }, [user, token]);

  const fetchEnrollments = async () => {
    setLoadingEnrollments(true);
    try {
      const data = await apiCall("GET", "/courses/enrollments", null, token);
      setEnrollments(data || []);
    } catch (err) {
      console.error("Failed to fetch enrollments:", err);
    }
    setLoadingEnrollments(false);
  };

  const handleEnroll = async (course) => {
    if (!user) {
      toast.error("Please sign in to enroll in courses");
      navigate("/portal");
      return;
    }
    
    if (user.user_type !== "student") {
      toast.error("Only students can enroll in courses");
      return;
    }

    setEnrollingCourse(course.id);
    try {
      await apiCall("POST", "/courses/enroll", {
        course_id: course.id,
        course_name: course.name
      }, token);
      
      toast.success(`Successfully enrolled in ${course.name}!`);
      await fetchEnrollments();
      setSelectedCourse(null);
    } catch (err) {
      toast.error(err.message || "Failed to enroll in course");
    }
    setEnrollingCourse(null);
  };

  const handleUnenroll = async (course) => {
    setEnrollingCourse(course.id);
    try {
      await apiCall("DELETE", `/courses/unenroll/${course.id}`, null, token);
      toast.success(`Successfully unenrolled from ${course.name}`);
      await fetchEnrollments();
    } catch (err) {
      toast.error(err.message || "Failed to unenroll from course");
    }
    setEnrollingCourse(null);
  };

  const isEnrolled = (courseId) => {
    return enrollments.some(e => e.course_id === courseId);
  };

  // Filter and sort courses
  const filteredCourses = coursesData
    .filter(course => {
      const matchesSearch = course.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           course.teacher.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           course.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = selectedCategory === "All" || course.category === selectedCategory;
      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case "name": return a.name.localeCompare(b.name);
        case "rating": return b.rating - a.rating;
        case "enrolled": return b.enrolled - a.enrolled;
        case "teacher": return a.teacher.localeCompare(b.teacher);
        default: return 0;
      }
    });

  const CourseCard = ({ course }) => {
    const enrolled = isEnrolled(course.id);
    const isLoading = enrollingCourse === course.id;
    
    return (
      <Card 
        className={`bg-white hover:shadow-lg transition-all duration-300 cursor-pointer border-l-4 group ${
          enrolled ? 'border-l-green-500 bg-green-50/30' : 'border-l-[#FF6B00]'
        }`}
        onClick={() => setSelectedCourse(course)}
        data-testid={`course-card-${course.id}`}
      >
        <CardContent className="p-5">
          <div className="flex justify-between items-start mb-3">
            <div className="flex gap-2 flex-wrap">
              <Badge className="bg-[#002147] text-white">{course.category}</Badge>
              {course.isElective && <Badge variant="outline" className="text-[#FF6B00] border-[#FF6B00]">Elective</Badge>}
              {enrolled && <Badge className="bg-green-500 text-white">Enrolled</Badge>}
            </div>
            <div className="flex items-center gap-1 text-yellow-500">
              <Star size={14} fill="currentColor" />
              <span className="text-sm font-medium">{course.rating}</span>
            </div>
          </div>
          
          <h3 className="text-lg font-bold text-[#002147] mb-2 group-hover:text-[#FF6B00] transition-colors">
            {course.name}
          </h3>
          
          <p className="text-sm text-gray-600 mb-4 line-clamp-2">{course.description}</p>
          
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2 text-gray-500">
              <User size={14} />
              <span>{course.teacher}</span>
            </div>
            <div className="flex items-center gap-2 text-gray-500">
              <Clock size={14} />
              <span>{course.schedule}</span>
            </div>
            <div className="flex items-center gap-2 text-gray-500">
              <MapPin size={14} />
              <span>{course.room}</span>
            </div>
          </div>
          
          <div className="mt-4 pt-3 border-t flex justify-between items-center">
            <div className="flex items-center gap-1 text-gray-500">
              <Users size={14} />
              <span className="text-sm">{course.enrolled} enrolled</span>
            </div>
            {course.isElective && user?.user_type === "student" && (
              <Button
                size="sm"
                className={enrolled ? "bg-red-500 hover:bg-red-600" : "bg-[#FF6B00] hover:bg-[#E65C00]"}
                onClick={(e) => {
                  e.stopPropagation();
                  enrolled ? handleUnenroll(course) : handleEnroll(course);
                }}
                disabled={isLoading}
              >
                {isLoading ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : enrolled ? (
                  "Drop"
                ) : (
                  "Enroll"
                )}
              </Button>
            )}
            {!course.isElective && (
              <Badge variant="outline" className="text-[#002147]">Required</Badge>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  const CourseListItem = ({ course }) => {
    const enrolled = isEnrolled(course.id);
    const isLoading = enrollingCourse === course.id;
    
    return (
      <Card 
        className={`bg-white hover:shadow-md transition-all duration-300 cursor-pointer mb-3 ${
          enrolled ? 'border-l-4 border-l-green-500' : ''
        }`}
        onClick={() => setSelectedCourse(course)}
        data-testid={`course-list-${course.id}`}
      >
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
              {course.teacherImage ? (
                <img src={course.teacherImage} alt={course.teacher} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-blue-50 flex items-center justify-center">
                  <UserCircle size={28} className="text-blue-300" />
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <h3 className="text-lg font-bold text-[#002147]">{course.name}</h3>
                <Badge className="bg-[#002147]/10 text-[#002147]">{course.category}</Badge>
                {course.isElective && <Badge variant="outline" className="text-[#FF6B00] border-[#FF6B00]">Elective</Badge>}
                {enrolled && <Badge className="bg-green-500 text-white">Enrolled</Badge>}
              </div>
              <p className="text-sm text-gray-600 truncate">{course.description}</p>
              <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                <span className="flex items-center gap-1"><User size={12} /> {course.teacher}</span>
                <span className="flex items-center gap-1"><Clock size={12} /> {course.schedule}</span>
                <span className="flex items-center gap-1"><MapPin size={12} /> {course.room}</span>
              </div>
            </div>
            <div className="text-right flex-shrink-0 flex flex-col items-end gap-2">
              <div className="flex items-center gap-1 text-yellow-500">
                <Star size={14} fill="currentColor" />
                <span className="font-medium">{course.rating}</span>
              </div>
              <p className="text-sm text-gray-500">{course.enrolled} enrolled</p>
              {course.isElective && user?.user_type === "student" && (
                <Button
                  size="sm"
                  className={enrolled ? "bg-red-500 hover:bg-red-600" : "bg-[#FF6B00] hover:bg-[#E65C00]"}
                  onClick={(e) => {
                    e.stopPropagation();
                    enrolled ? handleUnenroll(course) : handleEnroll(course);
                  }}
                  disabled={isLoading}
                >
                  {isLoading ? <Loader2 size={14} className="animate-spin" /> : enrolled ? "Drop" : "Enroll"}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const CourseDetailModal = ({ course, onClose }) => {
    const enrolled = isEnrolled(course.id);
    const isLoading = enrollingCourse === course.id;
    
    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
        <Card className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
          <CardHeader className="bg-gradient-to-r from-[#002147] to-[#003366] text-white rounded-t-lg">
            <div className="flex justify-between items-start">
              <div>
                <div className="flex gap-2 mb-2">
                  <Badge className="bg-[#FF6B00] text-white">{course.category}</Badge>
                  {course.isElective && <Badge className="bg-white/20 text-white">Elective</Badge>}
                  {enrolled && <Badge className="bg-green-500 text-white">Enrolled</Badge>}
                </div>
                <CardTitle className="text-2xl">{course.name}</CardTitle>
                <p className="text-white/80 mt-1">{course.level}</p>
              </div>
              <button onClick={onClose} className="text-white/80 hover:text-white text-2xl">&times;</button>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            {/* Teacher Info */}
            <div className="flex items-center gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
              {course.teacherImage ? (
                <img src={course.teacherImage} alt={course.teacher} className="w-16 h-16 rounded-full object-cover border-2 border-[#FF6B00]" />
              ) : (
                <div className="w-16 h-16 rounded-full border-2 border-orange-200 bg-orange-50 flex items-center justify-center flex-shrink-0">
                  <UserCircle size={28} className="text-orange-300" />
                </div>
              )}
              <div>
                <p className="text-sm text-gray-500">Instructor</p>
                <p className="text-lg font-semibold text-[#002147]">{course.teacher}</p>
                <div className="flex items-center gap-1 text-yellow-500 mt-1">
                  <Star size={14} fill="currentColor" />
                  <span className="text-sm font-medium">{course.rating} rating</span>
                </div>
              </div>
            </div>

            {/* Course Details Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="text-center p-3 bg-blue-50 rounded-lg">
                <Clock className="mx-auto text-blue-600 mb-1" size={20} />
                <p className="text-xs text-gray-500">Schedule</p>
                <p className="text-sm font-medium">{course.schedule}</p>
              </div>
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <MapPin className="mx-auto text-green-600 mb-1" size={20} />
                <p className="text-xs text-gray-500">Location</p>
                <p className="text-sm font-medium">{course.room}</p>
              </div>
              <div className="text-center p-3 bg-orange-50 rounded-lg">
                <GraduationCap className="mx-auto text-orange-600 mb-1" size={20} />
                <p className="text-xs text-gray-500">Credits</p>
                <p className="text-sm font-medium">{course.credits} Credits</p>
              </div>
              <div className="text-center p-3 bg-purple-50 rounded-lg">
                <Users className="mx-auto text-purple-600 mb-1" size={20} />
                <p className="text-xs text-gray-500">Enrolled</p>
                <p className="text-sm font-medium">{course.enrolled} Students</p>
              </div>
            </div>

            {/* Description */}
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-[#002147] mb-2">Course Description</h4>
              <p className="text-gray-600 leading-relaxed">{course.description}</p>
            </div>

            {/* Learning Objectives */}
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-[#002147] mb-2">Learning Objectives</h4>
              <ul className="space-y-2">
                {course.objectives.map((objective, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <div className="w-5 h-5 bg-[#FF6B00] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-white text-xs">{index + 1}</span>
                    </div>
                    <span className="text-gray-600">{objective}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Prerequisites */}
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-[#002147] mb-2">Prerequisites</h4>
              <p className="text-gray-600">{course.prerequisites}</p>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4 border-t">
              {course.isElective && user?.user_type === "student" ? (
                enrolled ? (
                  <>
                    <Button 
                      className="flex-1 bg-green-500 hover:bg-green-600"
                      disabled
                    >
                      <CheckCircle size={16} className="mr-2" /> Currently Enrolled
                    </Button>
                    <Button 
                      variant="outline"
                      className="border-red-500 text-red-500 hover:bg-red-50"
                      onClick={() => handleUnenroll(course)}
                      disabled={isLoading}
                    >
                      {isLoading ? <Loader2 size={16} className="animate-spin" /> : "Drop Course"}
                    </Button>
                  </>
                ) : (
                  <Button 
                    className="flex-1 bg-[#FF6B00] hover:bg-[#E65C00]"
                    onClick={() => handleEnroll(course)}
                    disabled={isLoading}
                    data-testid="enroll-btn"
                  >
                    {isLoading ? (
                      <Loader2 size={16} className="mr-2 animate-spin" />
                    ) : (
                      <GraduationCap size={16} className="mr-2" />
                    )}
                    Enroll in This Course
                  </Button>
                )
              ) : !course.isElective ? (
                <Button 
                  className="flex-1 bg-[#002147]"
                  disabled
                >
                  <CheckCircle size={16} className="mr-2" /> Required Course (Auto-enrolled)
                </Button>
              ) : !user ? (
                <Button 
                  className="flex-1 bg-[#FF6B00] hover:bg-[#E65C00]"
                  onClick={() => navigate("/portal")}
                >
                  Sign In to Enroll
                </Button>
              ) : (
                <Button 
                  className="flex-1 bg-gray-400"
                  disabled
                >
                  Students Only
                </Button>
              )}
              <Button 
                variant="outline" 
                className="border-[#002147] text-[#002147] hover:bg-[#002147] hover:text-white"
                onClick={() => {
                  if (user) {
                    navigate("/messages");
                  } else {
                    navigate("/portal");
                  }
                }}
              >
                Contact Teacher
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  // My Enrollments Section
  const MyEnrollments = () => {
    if (!user || user.user_type !== "student" || enrollments.length === 0) return null;
    
    return (
      <Card className="bg-white mb-6 border-2 border-green-500">
        <CardHeader className="bg-green-50">
          <CardTitle className="flex items-center gap-2 text-green-700">
            <CheckCircle size={20} /> My Enrolled Courses ({enrollments.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-2">
            {enrollments.map(enrollment => (
              <Badge 
                key={enrollment.id} 
                className="bg-green-100 text-green-800 py-1.5 px-3 cursor-pointer hover:bg-green-200"
                onClick={() => {
                  const course = coursesData.find(c => c.id === enrollment.course_id);
                  if (course) setSelectedCourse(course);
                }}
              >
                {enrollment.course_name}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-400 to-orange-400" data-testid="course-catalog-page">
      {/* Hero Section */}
      <div className="bg-[#002147] text-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-14 h-14 bg-[#FF6B00] rounded-full flex items-center justify-center">
              <BookOpen size={28} />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold">Course Catalog</h1>
              <p className="text-white/80">Young Christian Preparatory School / JHS</p>
            </div>
          </div>
          <p className="text-lg text-white/90 max-w-2xl">
            Explore our comprehensive curriculum aligned with Ghana Education Service standards. 
            {user?.user_type === "student" && " Enroll in elective courses to customize your learning experience."}
          </p>
        </div>
      </div>

      {/* Course Overview by Level */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <h2 className="text-2xl font-bold text-[#002147] mb-6 text-center">Our Curriculum by Level</h2>
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {/* KG Courses */}
          <Card className="bg-white shadow-lg hover:shadow-xl transition-shadow">
            <CardHeader className="bg-gradient-to-r from-pink-500 to-rose-400 text-white rounded-t-lg py-4">
              <CardTitle className="text-xl flex items-center gap-2">
                <GraduationCap size={24} /> KG (Kindergarten)
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="grid grid-cols-1 gap-2">
                <Button 
                  className="justify-start h-auto py-3 bg-gradient-to-b from-pink-400 to-pink-600 text-white transition-all rounded-full shadow-[0_4px_0_0_#be185d] hover:shadow-[0_2px_0_0_#be185d] hover:translate-y-[2px] active:shadow-none active:translate-y-[4px]"
                  onClick={() => navigate("/kg-subject/literacy")}
                  data-testid="kg-literacy-btn"
                >
                  <span className="font-medium">📚 Literacy</span>
                </Button>
                <Button 
                  className="justify-start h-auto py-3 bg-gradient-to-b from-blue-400 to-blue-600 text-white transition-all rounded-full shadow-[0_4px_0_0_#1d4ed8] hover:shadow-[0_2px_0_0_#1d4ed8] hover:translate-y-[2px] active:shadow-none active:translate-y-[4px]"
                  onClick={() => navigate("/kg-subject/numeracy")}
                  data-testid="kg-numeracy-btn"
                >
                  <span className="font-medium">🔢 Numeracy</span>
                </Button>
                <Button 
                  className="justify-start h-auto py-3 bg-gradient-to-b from-green-400 to-green-600 text-white transition-all rounded-full shadow-[0_4px_0_0_#15803d] hover:shadow-[0_2px_0_0_#15803d] hover:translate-y-[2px] active:shadow-none active:translate-y-[4px]"
                  onClick={() => navigate("/kg-subject/social-studies")}
                  data-testid="kg-social-studies-btn"
                >
                  <span className="font-medium">🌍 Social Studies</span>
                </Button>
                <Button 
                  className="justify-start h-auto py-3 bg-gradient-to-b from-purple-400 to-purple-600 text-white transition-all rounded-full shadow-[0_4px_0_0_#7e22ce] hover:shadow-[0_2px_0_0_#7e22ce] hover:translate-y-[2px] active:shadow-none active:translate-y-[4px]"
                  onClick={() => navigate("/kg-subject/computer-science")}
                  data-testid="kg-computer-science-btn"
                >
                  <span className="font-medium">💻 Computer Science and Engineering</span>
                </Button>
                <Button 
                  className="justify-start h-auto py-3 bg-gradient-to-b from-orange-400 to-orange-600 text-white transition-all rounded-full shadow-[0_4px_0_0_#c2410c] hover:shadow-[0_2px_0_0_#c2410c] hover:translate-y-[2px] active:shadow-none active:translate-y-[4px]"
                  onClick={() => navigate("/kg-subject/creative-arts")}
                  data-testid="kg-creative-arts-btn"
                >
                  <span className="font-medium">🎨 Creative Arts</span>
                </Button>
                <Button 
                  className="justify-start h-auto py-3 bg-gradient-to-b from-indigo-400 to-indigo-600 text-white transition-all rounded-full shadow-[0_4px_0_0_#4338ca] hover:shadow-[0_2px_0_0_#4338ca] hover:translate-y-[2px] active:shadow-none active:translate-y-[4px]"
                  onClick={() => navigate("/kg-subject/rme")}
                  data-testid="kg-rme-btn"
                >
                  <span className="font-medium">✝️ Religious and Moral Education (RME)</span>
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Basic School Courses */}
          <Card className="bg-white shadow-lg hover:shadow-xl transition-shadow">
            <CardHeader className="bg-gradient-to-r from-blue-500 to-cyan-400 text-white rounded-t-lg py-4">
              <CardTitle className="text-xl flex items-center gap-2">
                <BookOpen size={24} /> Basic School
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="grid grid-cols-1 gap-2">
                <Button 
                  className="justify-start h-auto py-2.5 bg-gradient-to-b from-blue-400 to-blue-600 hover:from-blue-500 hover:to-blue-700 text-white transition-all rounded-full shadow-[0_4px_0_0_#1e40af] hover:shadow-[0_2px_0_0_#1e40af] hover:translate-y-[2px] active:shadow-none active:translate-y-[4px] text-sm font-medium"
                  onClick={() => navigate("/basic-subject/mathematics")}
                  data-testid="basic-mathematics-btn"
                >
                  🔢 Mathematics
                </Button>
                <Button 
                  className="justify-start h-auto py-2.5 bg-gradient-to-b from-blue-400 to-blue-600 hover:from-blue-500 hover:to-blue-700 text-white transition-all rounded-full shadow-[0_4px_0_0_#1e40af] hover:shadow-[0_2px_0_0_#1e40af] hover:translate-y-[2px] active:shadow-none active:translate-y-[4px] text-sm font-medium"
                  onClick={() => navigate("/basic-subject/english-language")}
                  data-testid="basic-english-btn"
                >
                  📖 English Language
                </Button>
                <Button 
                  className="justify-start h-auto py-2.5 bg-gradient-to-b from-blue-400 to-blue-600 hover:from-blue-500 hover:to-blue-700 text-white transition-all rounded-full shadow-[0_4px_0_0_#1e40af] hover:shadow-[0_2px_0_0_#1e40af] hover:translate-y-[2px] active:shadow-none active:translate-y-[4px] text-sm font-medium"
                  onClick={() => navigate("/basic-subject/natural-science")}
                  data-testid="basic-science-btn"
                >
                  🔬 Natural Science
                </Button>
                <Button 
                  className="justify-start h-auto py-2.5 bg-gradient-to-b from-blue-400 to-blue-600 hover:from-blue-500 hover:to-blue-700 text-white transition-all rounded-full shadow-[0_4px_0_0_#1e40af] hover:shadow-[0_2px_0_0_#1e40af] hover:translate-y-[2px] active:shadow-none active:translate-y-[4px] text-sm font-medium"
                  onClick={() => navigate("/basic-subject/rme")}
                  data-testid="basic-rme-btn"
                >
                  ✝️ Religious and Moral Education (RME)
                </Button>
                <Button 
                  className="justify-start h-auto py-2.5 bg-gradient-to-b from-blue-400 to-blue-600 hover:from-blue-500 hover:to-blue-700 text-white transition-all rounded-full shadow-[0_4px_0_0_#1e40af] hover:shadow-[0_2px_0_0_#1e40af] hover:translate-y-[2px] active:shadow-none active:translate-y-[4px] text-sm font-medium"
                  onClick={() => navigate("/basic-subject/creative-arts")}
                  data-testid="basic-arts-btn"
                >
                  🎨 Creative Arts
                </Button>
                <Button 
                  className="justify-start h-auto py-2.5 bg-gradient-to-b from-blue-400 to-blue-600 hover:from-blue-500 hover:to-blue-700 text-white transition-all rounded-full shadow-[0_4px_0_0_#1e40af] hover:shadow-[0_2px_0_0_#1e40af] hover:translate-y-[2px] active:shadow-none active:translate-y-[4px] text-sm font-medium"
                  onClick={() => navigate("/basic-subject/history")}
                  data-testid="basic-history-btn"
                >
                  📜 History
                </Button>
                <Button 
                  className="justify-start h-auto py-2.5 bg-gradient-to-b from-blue-400 to-blue-600 hover:from-blue-500 hover:to-blue-700 text-white transition-all rounded-full shadow-[0_4px_0_0_#1e40af] hover:shadow-[0_2px_0_0_#1e40af] hover:translate-y-[2px] active:shadow-none active:translate-y-[4px] text-sm font-medium"
                  onClick={() => navigate("/basic-subject/french")}
                  data-testid="basic-french-btn"
                >
                  🇫🇷 French
                </Button>
                <Button 
                  className="justify-start h-auto py-2.5 bg-gradient-to-b from-blue-400 to-blue-600 hover:from-blue-500 hover:to-blue-700 text-white transition-all rounded-full shadow-[0_4px_0_0_#1e40af] hover:shadow-[0_2px_0_0_#1e40af] hover:translate-y-[2px] active:shadow-none active:translate-y-[4px] text-sm font-medium"
                  onClick={() => navigate("/basic-subject/fante")}
                  data-testid="basic-fante-btn"
                >
                  🇬🇭 Fante
                </Button>
                <Button 
                  className="justify-start h-auto py-2.5 bg-gradient-to-b from-blue-400 to-blue-600 hover:from-blue-500 hover:to-blue-700 text-white transition-all rounded-full shadow-[0_4px_0_0_#1e40af] hover:shadow-[0_2px_0_0_#1e40af] hover:translate-y-[2px] active:shadow-none active:translate-y-[4px] text-sm font-medium"
                  onClick={() => navigate("/basic-subject/computer-science")}
                  data-testid="basic-ict-btn"
                >
                  💻 Computer Science, Coding and Engineering
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* JHS Courses */}
          <Card className="bg-white shadow-lg hover:shadow-xl transition-shadow">
            <CardHeader className="bg-gradient-to-r from-[#002147] to-[#003366] text-white rounded-t-lg py-4">
              <CardTitle className="text-xl flex items-center gap-2">
                <Award size={24} /> JHS
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="grid grid-cols-1 gap-2">
                <Button 
                  className="justify-start h-auto py-2.5 bg-gradient-to-b from-[#002147] to-[#001a38] hover:from-[#003366] hover:to-[#002147] text-white transition-all rounded-full shadow-[0_4px_0_0_#000814] hover:shadow-[0_2px_0_0_#000814] hover:translate-y-[2px] active:shadow-none active:translate-y-[4px] text-sm font-medium"
                  onClick={() => navigate("/jhs-subject/mathematics")}
                  data-testid="jhs-mathematics-btn"
                >
                  🔢 Mathematics
                </Button>
                <Button 
                  className="justify-start h-auto py-2.5 bg-gradient-to-b from-[#002147] to-[#001a38] hover:from-[#003366] hover:to-[#002147] text-white transition-all rounded-full shadow-[0_4px_0_0_#000814] hover:shadow-[0_2px_0_0_#000814] hover:translate-y-[2px] active:shadow-none active:translate-y-[4px] text-sm font-medium"
                  onClick={() => navigate("/jhs-subject/english-language")}
                  data-testid="jhs-english-btn"
                >
                  📖 English Language
                </Button>
                <Button 
                  className="justify-start h-auto py-2.5 bg-gradient-to-b from-[#002147] to-[#001a38] hover:from-[#003366] hover:to-[#002147] text-white transition-all rounded-full shadow-[0_4px_0_0_#000814] hover:shadow-[0_2px_0_0_#000814] hover:translate-y-[2px] active:shadow-none active:translate-y-[4px] text-sm font-medium"
                  onClick={() => navigate("/jhs-subject/integrated-science")}
                  data-testid="jhs-science-btn"
                >
                  🔬 Integrated Science
                </Button>
                <Button 
                  className="justify-start h-auto py-2.5 bg-gradient-to-b from-[#002147] to-[#001a38] hover:from-[#003366] hover:to-[#002147] text-white transition-all rounded-full shadow-[0_4px_0_0_#000814] hover:shadow-[0_2px_0_0_#000814] hover:translate-y-[2px] active:shadow-none active:translate-y-[4px] text-sm font-medium"
                  onClick={() => navigate("/jhs-subject/social-studies")}
                  data-testid="jhs-social-btn"
                >
                  🌍 Social Studies
                </Button>
                <Button 
                  className="justify-start h-auto py-2.5 bg-gradient-to-b from-[#002147] to-[#001a38] hover:from-[#003366] hover:to-[#002147] text-white transition-all rounded-full shadow-[0_4px_0_0_#000814] hover:shadow-[0_2px_0_0_#000814] hover:translate-y-[2px] active:shadow-none active:translate-y-[4px] text-sm font-medium"
                  onClick={() => navigate("/jhs-subject/rme")}
                  data-testid="jhs-rme-btn"
                >
                  ✝️ Religious and Moral Education (RME)
                </Button>
                <Button 
                  className="justify-start h-auto py-2.5 bg-gradient-to-b from-[#002147] to-[#001a38] hover:from-[#003366] hover:to-[#002147] text-white transition-all rounded-full shadow-[0_4px_0_0_#000814] hover:shadow-[0_2px_0_0_#000814] hover:translate-y-[2px] active:shadow-none active:translate-y-[4px] text-sm font-medium"
                  onClick={() => navigate("/jhs-subject/creative-arts")}
                  data-testid="jhs-arts-btn"
                >
                  🎨 Creative Arts
                </Button>
                <Button 
                  className="justify-start h-auto py-2.5 bg-gradient-to-b from-[#002147] to-[#001a38] hover:from-[#003366] hover:to-[#002147] text-white transition-all rounded-full shadow-[0_4px_0_0_#000814] hover:shadow-[0_2px_0_0_#000814] hover:translate-y-[2px] active:shadow-none active:translate-y-[4px] text-sm font-medium"
                  onClick={() => navigate("/jhs-subject/career-technology")}
                  data-testid="jhs-career-btn"
                >
                  🔧 Career Technology
                </Button>
                <Button 
                  className="justify-start h-auto py-2.5 bg-gradient-to-b from-[#002147] to-[#001a38] hover:from-[#003366] hover:to-[#002147] text-white transition-all rounded-full shadow-[0_4px_0_0_#000814] hover:shadow-[0_2px_0_0_#000814] hover:translate-y-[2px] active:shadow-none active:translate-y-[4px] text-sm font-medium"
                  onClick={() => navigate("/jhs-subject/french")}
                  data-testid="jhs-french-btn"
                >
                  🇫🇷 French
                </Button>
                <Button 
                  className="justify-start h-auto py-2.5 bg-gradient-to-b from-[#002147] to-[#001a38] hover:from-[#003366] hover:to-[#002147] text-white transition-all rounded-full shadow-[0_4px_0_0_#000814] hover:shadow-[0_2px_0_0_#000814] hover:translate-y-[2px] active:shadow-none active:translate-y-[4px] text-sm font-medium"
                  onClick={() => navigate("/jhs-subject/fante")}
                  data-testid="jhs-fante-btn"
                >
                  🇬🇭 Fante
                </Button>
                <Button 
                  className="justify-start h-auto py-2.5 bg-gradient-to-b from-[#002147] to-[#001a38] hover:from-[#003366] hover:to-[#002147] text-white transition-all rounded-full shadow-[0_4px_0_0_#000814] hover:shadow-[0_2px_0_0_#000814] hover:translate-y-[2px] active:shadow-none active:translate-y-[4px] text-sm font-medium"
                  onClick={() => navigate("/jhs-subject/computer-science")}
                  data-testid="jhs-ict-btn"
                >
                  💻 Computer Science, Coding and Engineering
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Assignment Bulletin by Class */}
      <div className="max-w-7xl mx-auto px-4 pb-8">
        <Card className="bg-white shadow-lg" data-testid="assignment-bulletin">
          <CardHeader className="bg-gradient-to-r from-[#FF6B00] to-orange-500 text-white rounded-t-lg py-4">
            <CardTitle className="text-xl flex items-center gap-2">
              <Calendar size={24} /> Assignment Bulletin
            </CardTitle>
            <p className="text-white/80 text-sm mt-1">View assignments and homework by class</p>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* KG Section */}
              <div>
                <h3 className="text-sm font-bold text-[#002147] uppercase tracking-wide mb-3 flex items-center gap-2">
                  <GraduationCap size={16} className="text-pink-500" /> Kindergarten
                </h3>
                <div className="space-y-2">
                  {["Nursery", "KG1", "KG2"].map((cls) => (
                    <button
                      key={cls}
                      onClick={() => navigate(`/assignments/${cls.toLowerCase().replace(/\s/g, "-")}`)}
                      className="w-full text-left px-4 py-3 rounded-lg bg-pink-50 hover:bg-pink-100 border border-pink-200 text-[#002147] font-medium text-sm transition-all hover:translate-x-1 flex items-center justify-between group"
                      data-testid={`assignment-${cls.toLowerCase()}`}
                    >
                      <span className="flex items-center gap-2">
                        <BookOpen size={14} className="text-pink-500" />
                        {cls} Assignments
                      </span>
                      <ChevronDown size={14} className="text-pink-400 -rotate-90 group-hover:translate-x-1 transition-transform" />
                    </button>
                  ))}
                </div>
              </div>

              {/* Primary Section */}
              <div>
                <h3 className="text-sm font-bold text-[#002147] uppercase tracking-wide mb-3 flex items-center gap-2">
                  <BookOpen size={16} className="text-blue-500" /> Primary School
                </h3>
                <div className="space-y-2">
                  {["Class 1", "Class 2", "Class 3", "Class 4", "Class 5", "Class 6"].map((cls) => (
                    <button
                      key={cls}
                      onClick={() => navigate(`/assignments/${cls.toLowerCase().replace(/\s/g, "-")}`)}
                      className="w-full text-left px-4 py-3 rounded-lg bg-blue-50 hover:bg-blue-100 border border-blue-200 text-[#002147] font-medium text-sm transition-all hover:translate-x-1 flex items-center justify-between group"
                      data-testid={`assignment-${cls.toLowerCase().replace(/\s/g, "-")}`}
                    >
                      <span className="flex items-center gap-2">
                        <BookOpen size={14} className="text-blue-500" />
                        {cls} Assignments
                      </span>
                      <ChevronDown size={14} className="text-blue-400 -rotate-90 group-hover:translate-x-1 transition-transform" />
                    </button>
                  ))}
                </div>
              </div>

              {/* JHS Section */}
              <div>
                <h3 className="text-sm font-bold text-[#002147] uppercase tracking-wide mb-3 flex items-center gap-2">
                  <Award size={16} className="text-[#002147]" /> Junior High School
                </h3>
                <div className="space-y-2">
                  {["JHS1", "JHS2", "JHS3"].map((cls) => (
                    <button
                      key={cls}
                      onClick={() => navigate(`/assignments/${cls.toLowerCase()}`)}
                      className="w-full text-left px-4 py-3 rounded-lg bg-[#002147]/5 hover:bg-[#002147]/10 border border-[#002147]/20 text-[#002147] font-medium text-sm transition-all hover:translate-x-1 flex items-center justify-between group"
                      data-testid={`assignment-${cls.toLowerCase()}`}
                    >
                      <span className="flex items-center gap-2">
                        <BookOpen size={14} className="text-[#002147]" />
                        {cls} Assignments
                      </span>
                      <ChevronDown size={14} className="text-[#002147]/40 -rotate-90 group-hover:translate-x-1 transition-transform" />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Filter Section */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* My Enrollments */}
        <MyEnrollments />
        
        <Card className="bg-white mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <Input
                  placeholder="Search courses, teachers..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="course-search-input"
                />
              </div>
              
              {/* Category Filter */}
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-full md:w-48" data-testid="category-filter">
                  <Filter size={16} className="mr-2" />
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Sort By */}
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-full md:w-40" data-testid="sort-filter">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="name">Name</SelectItem>
                  <SelectItem value="rating">Rating</SelectItem>
                  <SelectItem value="enrolled">Most Popular</SelectItem>
                  <SelectItem value="teacher">Teacher</SelectItem>
                </SelectContent>
              </Select>

              {/* View Toggle */}
              <div className="flex border rounded-lg overflow-hidden">
                <Button
                  variant={viewMode === "grid" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setViewMode("grid")}
                  className={viewMode === "grid" ? "bg-[#002147]" : ""}
                >
                  <Grid size={18} />
                </Button>
                <Button
                  variant={viewMode === "list" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setViewMode("list")}
                  className={viewMode === "list" ? "bg-[#002147]" : ""}
                >
                  <List size={18} />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Results Count */}
        <div className="flex justify-between items-center mb-4">
          <p className="text-[#002147] font-medium">
            Showing {filteredCourses.length} of {coursesData.length} courses
            {user?.user_type === "student" && ` • ${filteredCourses.filter(c => c.isElective).length} electives available`}
          </p>
          {selectedCategory !== "All" && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setSelectedCategory("All")}
              className="text-[#FF6B00]"
            >
              Clear filter
            </Button>
          )}
        </div>

        {/* Category Tabs */}
        <div className="flex gap-2 flex-wrap mb-6">
          {categories.map(cat => (
            <Badge
              key={cat}
              variant={selectedCategory === cat ? "default" : "outline"}
              className={`cursor-pointer transition-all ${
                selectedCategory === cat 
                  ? "bg-[#002147] text-white" 
                  : "hover:bg-[#002147]/10"
              }`}
              onClick={() => setSelectedCategory(cat)}
            >
              {cat}
            </Badge>
          ))}
        </div>

        {/* Course Grid/List */}
        {viewMode === "grid" ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCourses.map(course => (
              <CourseCard key={course.id} course={course} />
            ))}
          </div>
        ) : (
          <div>
            {filteredCourses.map(course => (
              <CourseListItem key={course.id} course={course} />
            ))}
          </div>
        )}

        {/* No Results */}
        {filteredCourses.length === 0 && (
          <div className="text-center py-12">
            <BookOpen className="mx-auto text-gray-400 mb-4" size={48} />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">No courses found</h3>
            <p className="text-gray-500">Try adjusting your search or filter criteria</p>
          </div>
        )}
      </div>

      {/* Course Detail Modal */}
      {selectedCourse && (
        <CourseDetailModal 
          course={selectedCourse} 
          onClose={() => setSelectedCourse(null)} 
        />
      )}
    </div>
  );
};

export default CourseCatalogPage;
